// Module Gestion Utilisateurs - Interface Admin
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useSession } from '@/contexts/SessionContext';
import { UserServices } from '@/services/UserServices';
import { AuthServices } from '@/services/AuthServices';
import User from '@/models/User';
import {
  FiUsers, FiPlus, FiSearch, FiEdit, FiTrash2, FiLock,
  FiUnlock, FiShield, FiMail, FiPhone, FiCalendar,
  FiCheck, FiX, FiEye, FiDownload, FiUpload, FiAlertCircle
} from 'react-icons/fi';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${theme.spacing.lg};
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  ${props => props.variant === 'secondary' ? `
    background: transparent;
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.surface};
    &:hover {
      background: ${theme.colors.surface};
    }
  ` : `
    background: ${theme.colors.primary};
    color: white;
    &:hover {
      background: ${theme.colors.primary}DD;
      transform: translateY(-2px);
    }
  `}
`;

const SearchAndFilter = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md} 40px;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.text}60;
`;

const FilterSelect = styled.select`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing.lg};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.surface};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  margin-top: ${theme.spacing.xs};
`;

const TableContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${theme.colors.surface}50;
`;

const Th = styled.th`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text}80;
  text-transform: uppercase;
  border-bottom: 2px solid ${theme.colors.surface};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${theme.colors.surface};
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface}30;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UserName = styled.div`
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const UserEmail = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Badge = styled.div<{ color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${props => props.color || theme.colors.primary}20;
  color: ${props => props.color || theme.colors.primary};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  text-transform: uppercase;
`;

const StatusDot = styled.div<{ status: 'active' | 'inactive' | 'pending' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success;
      case 'inactive': return theme.colors.text + '40';
      case 'pending': return theme.colors.warning;
      default: return theme.colors.text;
    }
  }};
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
`;

const IconButton = styled.button`
  padding: ${theme.spacing.sm};
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text}60;
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.primary};
  }
`;

const Modal = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const Label = styled.label`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  background: white;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${theme.spacing.xl};
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing['2xl']};
  color: ${theme.colors.text}60;
  font-size: ${theme.typography.sizes.lg};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}10;
  color: ${theme.colors.error};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.error};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success}10;
  color: ${theme.colors.success};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.md};
  border-left: 4px solid ${theme.colors.success};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const RolesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  max-height: 200px;
  overflow-y: auto;
  padding: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface}30;
`;

const RoleCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const RoleLabel = styled.span`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  flex: 1;
`;

const RoleCode = styled.code`
  font-size: ${theme.typography.sizes.xs};
  background: ${theme.colors.primary}20;
  color: ${theme.colors.primary};
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.sm};
`;

interface Role {
  id: number;
  name: string;
  code: string;
}

export const UserManagement: React.FC = () => {
  const { token } = useSession();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Roles state
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phonenumber: '',
    username: '',
    is_active: true,
    roles: [] as number[]
  });

  // Charger les utilisateurs et rôles au montage du composant
  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    if (!token) {
      setError('Token d\'authentification manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await UserServices.getUsers(token);
      const result = response as { success: boolean; data?: User[]; message?: string };

      if (result.success && result.data) {
        setUsers(result.data);
      } else {
        setError(result.message || 'Erreur lors du chargement des utilisateurs');
      }
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await AuthServices.getRoles();
      const result = response as { success: boolean; data?: Role[]; message?: string };

      if (result.success && result.data) {
        setAvailableRoles(result.data);
      } else {
        console.error('Erreur lors du chargement des rôles:', result.message);
      }
    } catch (err: any) {
      console.error('Error loading roles:', err);
    } finally {
      setLoadingRoles(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleRoleToggle = (roleId: number) => {
    setFormData(prev => {
      const roles = prev.roles.includes(roleId)
        ? prev.roles.filter(id => id !== roleId)
        : [...prev.roles, roleId];
      return { ...prev, roles };
    });
  };

  const getUserRoles = (user: User): string => {
    if (user.role_codes && user.role_codes.length > 0) {
      return user.role_codes.join(', ');
    }
    if (user.is_superuser) return 'Super Admin';
    if (user.is_staff) return 'Staff';
    return 'Utilisateur';
  };

  const getRoleColor = (user: User): string => {
    if (user.is_superuser) return theme.colors.error;
    if (user.is_staff) return theme.colors.primary;
    if (user.role_codes && user.role_codes.length > 0) {
      return theme.colors.info;
    }
    return theme.colors.text;
  };

  const getStatusLabel = (isActive: boolean): string => {
    return isActive ? 'Actif' : 'Inactif';
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && user.is_active) ||
                         (statusFilter === 'inactive' && !user.is_active);

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
    staff: users.filter(u => u.is_staff).length,
    superusers: users.filter(u => u.is_superuser).length
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phonenumber: '',
      username: '',
      is_active: true,
      roles: []
    });
    setSelectedUser(null);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('Token d\'authentification manquant');
      return;
    }

    try {
      setError(null);
      const response = await UserServices.addUser(formData, token);
      const result = response as { success: boolean; data?: User; message?: string };

      if (result.success) {
        showSuccess('Utilisateur créé avec succès !');
        setShowCreateModal(false);
        resetForm();
        await loadUsers();
      } else {
        setError(result.message || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phonenumber: user.phonenumber || '',
      username: user.username,
      is_active: user.is_active,
      roles: user.roles || []
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedUser) {
      setError('Token ou utilisateur manquant');
      return;
    }

    try {
      setError(null);
      const response = await UserServices.updateUser(selectedUser.id, formData, token);
      const result = response as { success: boolean; data?: User; message?: string };

      if (result.success) {
        showSuccess('Utilisateur mis à jour avec succès !');
        setShowEditModal(false);
        resetForm();
        await loadUsers();
      } else {
        setError(result.message || 'Erreur lors de la mise à jour de l\'utilisateur');
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      setError(err.message || 'Erreur lors de la mise à jour de l\'utilisateur');
    }
  };

  const handleToggleStatus = async (user: User) => {
    if (!token) {
      setError('Token d\'authentification manquant');
      return;
    }

    const action = user.is_active ? 'désactiver' : 'activer';
    if (!window.confirm(`Voulez-vous vraiment ${action} cet utilisateur ?`)) {
      return;
    }

    try {
      setError(null);
      const response = await UserServices.toggleUserStatus(user.id, token);
      const result = response as { success: boolean; message?: string; data?: { is_active: boolean } };

      if (result.success) {
        showSuccess(result.message || `Utilisateur ${action === 'désactiver' ? 'désactivé' : 'activé'} avec succès !`);
        await loadUsers();
      } else {
        setError(result.message || `Erreur lors de l'${action === 'désactiver' ? 'désactivation' : 'activation'}`);
      }
    } catch (err: any) {
      console.error('Error toggling user status:', err);
      setError(err.message || `Erreur lors de l'${action === 'désactiver' ? 'désactivation' : 'activation'}`);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!token) {
      setError('Token d\'authentification manquant');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.first_name} ${user.last_name} ? Cette action est irréversible.`)) {
      return;
    }

    try {
      setError(null);
      const response = await UserServices.deleteUser(user.id, token);
      const result = response as { success: boolean; message?: string };

      if (result.success) {
        showSuccess('Utilisateur supprimé avec succès !');
        await loadUsers();
      } else {
        setError(result.message || 'Erreur lors de la suppression de l\'utilisateur');
      }
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner>
          <FiUsers size={32} style={{ marginRight: theme.spacing.md }} />
          Chargement des utilisateurs...
        </LoadingSpinner>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container>
        {error && (
          <ErrorMessage>
            <FiAlertCircle size={20} />
            {error}
          </ErrorMessage>
        )}

        {successMessage && (
          <SuccessMessage>
            <FiCheck size={20} />
            {successMessage}
          </SuccessMessage>
        )}

        <Header>
          <HeaderTop>
            <TitleSection>
              <Title>Gestion des Utilisateurs</Title>
              <Subtitle>CRUD utilisateurs et gestion des permissions</Subtitle>
            </TitleSection>
            <ActionButtons>
              <Button variant="secondary">
                <FiDownload /> Exporter
              </Button>
              <Button variant="secondary">
                <FiUpload /> Import Masse
              </Button>
              <Button onClick={() => setShowCreateModal(true)}>
                <FiPlus /> Nouvel Utilisateur
              </Button>
            </ActionButtons>
          </HeaderTop>

          <SearchAndFilter>
            <SearchBox>
              <SearchIcon><FiSearch /></SearchIcon>
              <SearchInput
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </FilterSelect>
          </SearchAndFilter>

          <StatsRow>
            <StatItem>
              <StatValue>{stats.total}</StatValue>
              <StatLabel>Total</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue style={{ color: theme.colors.success }}>{stats.active}</StatValue>
              <StatLabel>Actifs</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue style={{ color: theme.colors.text + '60' }}>{stats.inactive}</StatValue>
              <StatLabel>Inactifs</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue style={{ color: theme.colors.primary }}>{stats.staff}</StatValue>
              <StatLabel>Staff</StatLabel>
            </StatItem>
            <StatItem>
              <StatValue style={{ color: theme.colors.error }}>{stats.superusers}</StatValue>
              <StatLabel>Admins</StatLabel>
            </StatItem>
          </StatsRow>
        </Header>

        <TableContainer>
          <Table>
            <Thead>
              <tr>
                <Th>Utilisateur</Th>
                <Th>Username</Th>
                <Th>Rôle</Th>
                <Th>Statut</Th>
                <Th>Dernière connexion</Th>
                <Th>Date création</Th>
                <Th>Actions</Th>
              </tr>
            </Thead>
            <Tbody>
              {filteredUsers.length === 0 ? (
                <Tr>
                  <Td colSpan={7} style={{ textAlign: 'center', padding: theme.spacing['2xl'], color: theme.colors.text + '60' }}>
                    Aucun utilisateur trouvé
                  </Td>
                </Tr>
              ) : (
                filteredUsers.map(user => (
                  <Tr key={user.id}>
                    <Td>
                      <UserInfo>
                        <UserName>{user.first_name} {user.last_name}</UserName>
                        <UserEmail>
                          <FiMail size={12} />
                          {user.email}
                        </UserEmail>
                        {user.phonenumber && (
                          <UserEmail>
                            <FiPhone size={12} />
                            {user.phonenumber}
                          </UserEmail>
                        )}
                      </UserInfo>
                    </Td>
                    <Td>
                      <code style={{ fontSize: theme.typography.sizes.xs, background: theme.colors.surface, padding: '4px 8px', borderRadius: theme.borderRadius.sm }}>
                        {user.username}
                      </code>
                    </Td>
                    <Td>
                      <Badge color={getRoleColor(user)}>
                        <FiShield size={12} />
                        {getUserRoles(user)}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge color={user.is_active ? theme.colors.success : theme.colors.text}>
                        <StatusDot status={user.is_active ? 'active' : 'inactive'} />
                        {getStatusLabel(user.is_active)}
                      </Badge>
                    </Td>
                    <Td>
                      <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '80' }}>
                        {user.last_login ? (
                          <>
                            <div>{new Date(user.last_login).toLocaleDateString('fr-FR')}</div>
                            <div>{new Date(user.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                          </>
                        ) : (
                          <span style={{ color: theme.colors.text + '40' }}>Jamais</span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '80' }}>
                        {new Date(user.date_joined).toLocaleDateString('fr-FR')}
                      </div>
                    </Td>
                    <Td>
                      <ActionsCell>
                        <IconButton onClick={() => handleEditUser(user)} title="Modifier">
                          <FiEdit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleToggleStatus(user)}
                          title={user.is_active ? 'Désactiver' : 'Activer'}
                        >
                          {user.is_active ? <FiLock /> : <FiUnlock />}
                        </IconButton>
                        <IconButton onClick={() => handleDeleteUser(user)} title="Supprimer">
                          <FiTrash2 />
                        </IconButton>
                      </ActionsCell>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        <Modal show={showCreateModal} onClick={() => { setShowCreateModal(false); resetForm(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Nouvel Utilisateur</ModalTitle>
            <Form onSubmit={handleCreateUser}>
              <FormGroup>
                <Label>Prénom *</Label>
                <Input
                  type="text"
                  placeholder="Jean"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Nom *</Label>
                <Input
                  type="text"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Username *</Label>
                <Input
                  type="text"
                  placeholder="jean.dupont"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="jean.dupont@organization.fr"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  placeholder="+221 6 12 34 56 78"
                  value={formData.phonenumber}
                  onChange={(e) => setFormData({ ...formData, phonenumber: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Rôles</Label>
                {loadingRoles ? (
                  <div style={{ padding: theme.spacing.md, color: theme.colors.text + '60' }}>
                    Chargement des rôles...
                  </div>
                ) : (
                  <RolesContainer>
                    {availableRoles.length === 0 ? (
                      <div style={{ padding: theme.spacing.md, color: theme.colors.text + '60', textAlign: 'center' }}>
                        Aucun rôle disponible
                      </div>
                    ) : (
                      availableRoles.map(role => (
                        <RoleCheckboxItem key={role.id}>
                          <Checkbox
                            type="checkbox"
                            checked={formData.roles.includes(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                          />
                          <RoleLabel>{role.name}</RoleLabel>
                          <RoleCode>{role.code}</RoleCode>
                        </RoleCheckboxItem>
                      ))
                    )}
                  </RolesContainer>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Statut initial</Label>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </Select>
              </FormGroup>
              <ModalActions>
                <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Annuler
                </Button>
                <Button type="submit">
                  <FiCheck /> Créer l'utilisateur
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>

        <Modal show={showEditModal} onClick={() => { setShowEditModal(false); resetForm(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Modifier l'utilisateur</ModalTitle>
            <Form onSubmit={handleUpdateUser}>
              <FormGroup>
                <Label>Prénom *</Label>
                <Input
                  type="text"
                  placeholder="Jean"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Nom *</Label>
                <Input
                  type="text"
                  placeholder="Dupont"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Username *</Label>
                <Input
                  type="text"
                  placeholder="jean.dupont"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="jean.dupont@organization.fr"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  placeholder="+221 6 12 34 56 78"
                  value={formData.phonenumber}
                  onChange={(e) => setFormData({ ...formData, phonenumber: e.target.value })}
                />
              </FormGroup>
              <FormGroup>
                <Label>Rôles</Label>
                {loadingRoles ? (
                  <div style={{ padding: theme.spacing.md, color: theme.colors.text + '60' }}>
                    Chargement des rôles...
                  </div>
                ) : (
                  <RolesContainer>
                    {availableRoles.length === 0 ? (
                      <div style={{ padding: theme.spacing.md, color: theme.colors.text + '60', textAlign: 'center' }}>
                        Aucun rôle disponible
                      </div>
                    ) : (
                      availableRoles.map(role => (
                        <RoleCheckboxItem key={role.id}>
                          <Checkbox
                            type="checkbox"
                            checked={formData.roles.includes(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                          />
                          <RoleLabel>{role.name}</RoleLabel>
                          <RoleCode>{role.code}</RoleCode>
                        </RoleCheckboxItem>
                      ))
                    )}
                  </RolesContainer>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Statut</Label>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </Select>
              </FormGroup>
              <ModalActions>
                <Button type="button" variant="secondary" onClick={() => { setShowEditModal(false); resetForm(); }}>
                  Annuler
                </Button>
                <Button type="submit">
                  <FiCheck /> Mettre à jour
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>
      </Container>
    </DashboardLayout>
  );
};

export default UserManagement;
